import React from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useSnackbar } from 'notistack'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { usePutLuckyDrawEntryList } from '../../api/lucky-draw'

/** What the server read back, so the uploader can see the sheet was understood as they meant it. */
const describeUpload = ({ companyCount, totalTickets, merged }) => {
  const summary = `Uploaded ${companyCount} companies, ${totalTickets} tickets.`

  return merged > 0 ? `${summary} ${merged} duplicate row(s) summed.` : summary
}

/**
 * A manual draw's entry list, replaced from a spreadsheet. Locked once a prize has been drawn — a
 * company is its name, so a winner respelt on a new sheet would escape its caps.
 */
const Upload = ({ id, hasWinners, onUploaded }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: uploadEntryList, isLoading: isUploading } = usePutLuckyDrawEntryList(id)

  const isBlocked = isUploading || hasWinners

  const handleFile = async (event) => {
    const file = event.target.files?.[0]

    // Cleared so picking the same file again still fires a change.
    event.target.value = ''
    if (!file) return

    try {
      const { data } = await uploadEntryList(file)

      enqueueSnackbar(describeUpload(data), { variant: 'success' })
      onUploaded()
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  return (
    <Box mb={3}>
      <Button
        component='label'
        variant='outlined'
        startIcon={
          isUploading ? <CircularProgress size={16} /> : <UploadFileIcon fontSize='small' />
        }
        disabled={isBlocked}
      >
        {hasWinners ? 'LIST LOCKED' : 'UPLOAD XLSX'}
        {/* Rendered only when usable, so a disabled button cannot still open a file picker. */}
        {!isBlocked && <input hidden type='file' accept='.xlsx,.xls' onChange={handleFile} />}
      </Button>
      <Typography variant='body2' color='text.secondary' mt={1}>
        {hasWinners
          ? 'A prize has already been drawn, so the entry list can no longer change.'
          : 'Two columns, Name and Tickets, one row per company. Uploading replaces the whole list.'}
      </Typography>
    </Box>
  )
}

export default Upload
